import type { RecipeDefinition, EssenceAllocation, EssenceType } from './types/essence';
import { createEmptyEssencePool, resolveTotalEssence, hasMinimumEssence } from './types/essence';
import { CRYSTAL_EFFECT_DURATION_MS, RESONANCE_RESTORE_FRACTION } from './crystal-use';
import { INSIGHT_FRAGMENT_REVEAL_RADIUS_TILES, WEAK_POINT_DETECTED_CHANCE, WEAK_POINT_VISIBLE_CHANCE } from './crystal-effects';
import type { CrystalExplosion } from './types/crystals';

/**
 * A pure Fury explosion: enemies within 1.5 tiles take 30% of the
 * triggering damage and the user's allies 10%. First-pass numbers.
 */
const FURY_EXPLOSION: CrystalExplosion = { radiusTiles: 1.5, damageFraction: 0.3, allyDamageFraction: 0.1 };

/**
 * Defines all available crystal recipes.
 * These are the "standard" recipes; players can discover more through trial and error.
 */
export class RecipeSystem {
  private recipes: Map<string, RecipeDefinition> = new Map();
  private discoveredRecipeIds: Set<string> = new Set();

  constructor() {
    this.initializeRecipes();
  }

  private initializeRecipes(): void {
    // ============ TIER 1: SPLINTERS (100 essence) ============

    this.registerRecipe({
      id: 'vitality-splinter-courage',
      name: 'Vitality Splinter (Courage)',
      size: 'splinter',
      crystalType: 'vitality',
      ingredients: { courage: 60, resonance: 40 },
      isDiscoveredByDefault: true,
      description: '+25% damage, +10% damage reduction for 20s',
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.splinter, self: { outgoingDamage: 0.25, damageTaken: -0.1 } },
    });

    this.registerRecipe({
      id: 'vitality-splinter-sorrow',
      name: 'Vitality Splinter (Sorrow)',
      size: 'splinter',
      crystalType: 'vitality',
      ingredients: { sorrow: 60, resonance: 40 },
      isDiscoveredByDefault: true,
      description: 'Restore 20% of your essence pool, +15% healing received for 20s',
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.splinter, restoreFraction: 0.2, self: { healingReceived: 0.15 } },
    });

    this.registerRecipe({
      id: 'insight-splinter',
      name: 'Insight Splinter',
      size: 'splinter',
      crystalType: 'insight',
      ingredients: { memory: 50, resonance: 50 },
      isDiscoveredByDefault: true,
      description: '+20% praxis gain, detect weak points for 20s',
      effect: {
        durationMs: CRYSTAL_EFFECT_DURATION_MS.splinter,
        self: { praxisGain: 0.2 },
        behaviors: { revealWeakPoints: { chance: WEAK_POINT_DETECTED_CHANCE } },
      },
    });

    this.registerRecipe({
      id: 'warding-splinter',
      name: 'Warding Splinter',
      size: 'splinter',
      crystalType: 'warding',
      ingredients: { truth: 40, resonance: 60 },
      isDiscoveredByDefault: true,
      description: 'See hidden enemies, detect deception for 20s',
    });

    this.registerRecipe({
      id: 'fury-splinter',
      name: 'Fury Splinter',
      size: 'splinter',
      crystalType: 'fury',
      ingredients: { fury: 70, resonance: 30 },
      isDiscoveredByDefault: true,
      description: '+40% damage, -10% defense for 20s',
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.splinter, self: { outgoingDamage: 0.4, defense: -0.1 } },
    });

    this.registerRecipe({
      id: 'resonance-splinter',
      name: 'Resonance Splinter',
      size: 'splinter',
      crystalType: 'resonance',
      ingredients: { resonance: 100 },
      isDiscoveredByDefault: true,
      description: 'Restore 15% of your essence pool',
      effect: { durationMs: 0, restoreFraction: RESONANCE_RESTORE_FRACTION.splinter },
    });

    // ============ TIER 2: FRAGMENTS (200 essence) ============

    this.registerRecipe({
      id: 'vitality-fragment-courage',
      name: 'Vitality Fragment (Courage)',
      size: 'fragment',
      crystalType: 'vitality',
      ingredients: { courage: 120, resonance: 80 },
      isDiscoveredByDefault: true,
      description: '+35% damage, +15% damage reduction, allies +15% damage for 1 min',
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.fragment, self: { outgoingDamage: 0.35, damageTaken: -0.15 }, allies: { outgoingDamage: 0.15 } },
    });

    this.registerRecipe({
      id: 'vitality-fragment-sorrow',
      name: 'Vitality Fragment (Sorrow)',
      size: 'fragment',
      crystalType: 'vitality',
      ingredients: { sorrow: 120, resonance: 80 },
      isDiscoveredByDefault: true,
      description: 'Restore 40% of your essence pool, +25% healing received, allies -10% damage taken for 1 min',
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.fragment, restoreFraction: 0.4, self: { healingReceived: 0.25 }, allies: { damageTaken: -0.1 } },
    });

    this.registerRecipe({
      id: 'insight-fragment',
      name: 'Insight Fragment',
      size: 'fragment',
      crystalType: 'insight',
      ingredients: { memory: 100, resonance: 100 },
      isDiscoveredByDefault: true,
      description: 'Partial map reveal, weak points always visible, +30% praxis gain for 1 min',
      effect: {
        durationMs: CRYSTAL_EFFECT_DURATION_MS.fragment,
        self: { praxisGain: 0.3 },
        behaviors: { revealWeakPoints: { chance: WEAK_POINT_VISIBLE_CHANCE } },
        revealMap: { radiusTiles: INSIGHT_FRAGMENT_REVEAL_RADIUS_TILES },
      },
    });

    this.registerRecipe({
      id: 'warding-fragment',
      name: 'Warding Fragment',
      size: 'fragment',
      crystalType: 'warding',
      ingredients: { truth: 80, resonance: 120 },
      isDiscoveredByDefault: true,
      description: 'See illusions/hidden, detect all items, +20% damage to "true" enemies for 1 min',
    });

    this.registerRecipe({
      id: 'fury-fragment',
      name: 'Fury Fragment',
      size: 'fragment',
      crystalType: 'fury',
      ingredients: { fury: 150, resonance: 50 },
      isDiscoveredByDefault: true,
      description: '+50% damage, -15% defense, your hits explode (hurting allies too) for 1 min',
      effect: {
        durationMs: CRYSTAL_EFFECT_DURATION_MS.fragment,
        self: { outgoingDamage: 0.5, defense: -0.15 },
        behaviors: { explodeOnHit: FURY_EXPLOSION },
      },
    });

    this.registerRecipe({
      id: 'resonance-fragment',
      name: 'Resonance Fragment',
      size: 'fragment',
      crystalType: 'resonance',
      ingredients: { resonance: 200 },
      isDiscoveredByDefault: true,
      description: 'Restore 30% of your essence pool',
      effect: { durationMs: 0, restoreFraction: RESONANCE_RESTORE_FRACTION.fragment },
    });

    // ============ TIER 3: COMPLETE (300 essence) ============

    this.registerRecipe({
      id: 'vitality-complete-courage',
      name: 'Vitality Complete (Courage)',
      size: 'complete',
      crystalType: 'vitality',
      ingredients: { courage: 180, resonance: 120 },
      isDiscoveredByDefault: true,
      description: '+50% damage, +25% damage reduction, aura +25% damage to all allies for 3 min',
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.complete, self: { outgoingDamage: 0.5, damageTaken: -0.25 }, allies: { outgoingDamage: 0.25 } },
    });

    this.registerRecipe({
      id: 'vitality-complete-sorrow',
      name: 'Vitality Complete (Sorrow)',
      size: 'complete',
      crystalType: 'vitality',
      ingredients: { sorrow: 180, resonance: 120 },
      isDiscoveredByDefault: true,
      description: 'Full heal all allies, +40% healing received, -15% damage taken aura for 3 min',
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.complete, restoreFraction: 1, allyRestoreFraction: 1, self: { healingReceived: 0.4, damageTaken: -0.15 }, allies: { damageTaken: -0.15 } },
    });

    this.registerRecipe({
      id: 'insight-complete',
      name: 'Insight Complete',
      size: 'complete',
      crystalType: 'insight',
      ingredients: { memory: 150, resonance: 150 },
      isDiscoveredByDefault: true,
      description: 'Full Crucible map revealed, all hidden truths visible, echo-items become real, +50% praxis gain for 3 min',
      // TODO: hidden truths and echo-items wait on #125.
      effect: {
        durationMs: CRYSTAL_EFFECT_DURATION_MS.complete,
        self: { praxisGain: 0.5 },
        behaviors: { revealWeakPoints: { chance: WEAK_POINT_VISIBLE_CHANCE } },
        revealMap: {},
      },
    });

    this.registerRecipe({
      id: 'warding-complete',
      name: 'Warding Complete',
      size: 'complete',
      crystalType: 'warding',
      ingredients: { truth: 120, resonance: 180 },
      isDiscoveredByDefault: true,
      description: "Full Veil-sight, cannot be deceived, see enemies' true forms for 3 min",
    });

    this.registerRecipe({
      id: 'fury-complete',
      name: 'Fury Complete',
      size: 'complete',
      crystalType: 'fury',
      ingredients: { fury: 200, resonance: 100 },
      isDiscoveredByDefault: true,
      description: '+70% damage, -20% defense, continuous explosions (hurting allies too), enemies target you for 3 min',
      effect: {
        durationMs: CRYSTAL_EFFECT_DURATION_MS.complete,
        self: { outgoingDamage: 0.7, defense: -0.2 },
        behaviors: {
          explodeOnHit: FURY_EXPLOSION,
          pulse: { ...FURY_EXPLOSION, intervalMs: 2000 },
          drawAggression: { sightRangeMultiplier: 1.5 },
        },
      },
    });

    this.registerRecipe({
      id: 'resonance-complete',
      name: 'Resonance Complete',
      size: 'complete',
      crystalType: 'resonance',
      ingredients: { resonance: 300 },
      isDiscoveredByDefault: true,
      description: 'Restore 50% of your essence pool',
      effect: { durationMs: 0, restoreFraction: RESONANCE_RESTORE_FRACTION.complete },
    });

    // ============ HYBRID: HARMONY SERIES ============

    this.registerRecipe({
      id: 'harmony-splinter',
      name: 'Harmony Splinter',
      size: 'splinter',
      crystalType: 'vitality',
      ingredients: { courage: 30, sorrow: 30, resonance: 40 },
      isDiscoveredByDefault: false,
      description: '+15% damage, +15% healing received for 20s',
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.splinter, self: { outgoingDamage: 0.15, healingReceived: 0.15 } },
    });

    this.registerRecipe({
      id: 'harmony-fragment',
      name: 'Harmony Fragment',
      size: 'fragment',
      crystalType: 'vitality',
      ingredients: { courage: 60, sorrow: 60, resonance: 80 },
      isDiscoveredByDefault: false,
      description: '+25% damage, +25% healing received for 1 min',
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.fragment, self: { outgoingDamage: 0.25, healingReceived: 0.25 } },
    });

    this.registerRecipe({
      id: 'harmony-complete',
      name: 'Harmony Complete',
      size: 'complete',
      crystalType: 'vitality',
      ingredients: { courage: 90, sorrow: 90, resonance: 120 },
      isDiscoveredByDefault: false,
      description: '+40% damage, +40% healing received for 3 min',
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.complete, self: { outgoingDamage: 0.4, healingReceived: 0.4 } },
    });

    // ============ HYBRID: REVELATION SERIES ============

    this.registerRecipe({
      id: 'revelation-splinter',
      name: 'Revelation Splinter',
      size: 'splinter',
      crystalType: 'insight',
      ingredients: { memory: 30, truth: 30, resonance: 40 },
      isDiscoveredByDefault: false,
      description: 'Detect weak points + hidden enemies for 20s',
      // TODO: only the weak points are live; hidden enemy detection waits on #125.
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.splinter, behaviors: { revealWeakPoints: { chance: WEAK_POINT_DETECTED_CHANCE } } },
    });

    this.registerRecipe({
      id: 'revelation-fragment',
      name: 'Revelation Fragment',
      size: 'fragment',
      crystalType: 'insight',
      ingredients: { memory: 60, truth: 60, resonance: 80 },
      isDiscoveredByDefault: false,
      description: 'Map + weak points + deception detect for 1 min',
      // TODO: only the weak points are live. Map knowledge (#124) has landed, so the map reveal
      // only needs a `revealMap` like Insight Fragment's; deception detection waits on #125.
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.fragment, behaviors: { revealWeakPoints: { chance: WEAK_POINT_VISIBLE_CHANCE } } },
    });

    this.registerRecipe({
      id: 'revelation-complete',
      name: 'Revelation Complete',
      size: 'complete',
      crystalType: 'insight',
      ingredients: { memory: 90, truth: 90, resonance: 120 },
      isDiscoveredByDefault: false,
      description: 'Full omniscience for 3 min',
      // TODO: only the weak points are live. Map knowledge (#124) has landed, so the map reveal
      // only needs a `revealMap` like Insight Complete's; the rest of full omniscience waits on #125.
      effect: { durationMs: CRYSTAL_EFFECT_DURATION_MS.complete, behaviors: { revealWeakPoints: { chance: WEAK_POINT_VISIBLE_CHANCE } } },
    });

    // ============ HYBRID: UNSTABLE SERIES ============

    this.registerRecipe({
      id: 'unstable-splinter',
      name: 'Unstable Splinter',
      size: 'splinter',
      crystalType: 'fury',
      ingredients: { fury: 80, resonance: 20 },
      isDiscoveredByDefault: false,
      description: '+60% Damage, 40% chance to hurt self for 20s',
    });

    this.registerRecipe({
      id: 'unstable-fragment',
      name: 'Unstable Fragment',
      size: 'fragment',
      crystalType: 'fury',
      ingredients: { fury: 160, resonance: 40 },
      isDiscoveredByDefault: false,
      description: '+80% Damage, you take 20% splash damage for 1 min',
    });

    this.registerRecipe({
      id: 'unstable-complete',
      name: 'Unstable Complete',
      size: 'complete',
      crystalType: 'fury',
      ingredients: { fury: 240, resonance: 60 },
      isDiscoveredByDefault: false,
      description: '+100% Damage, 30% self-damage for 3 min',
    });

    // Discover default recipes
    for (const recipe of this.recipes.values()) {
      if (recipe.isDiscoveredByDefault) {
        this.discoveredRecipeIds.add(recipe.id);
      }
    }
  }

  private registerRecipe(recipe: RecipeDefinition): void {
    this.recipes.set(recipe.id, recipe);
  }

  /**
   * Get a recipe by ID.
   */
  getRecipe(id: string): RecipeDefinition | null {
    return this.recipes.get(id) ?? null;
  }

  /**
   * Get all available recipes.
   */
  getAllRecipes(): readonly RecipeDefinition[] {
    return Array.from(this.recipes.values());
  }

  /**
   * Get only discovered recipes.
   */
  getDiscoveredRecipes(): readonly RecipeDefinition[] {
    return Array.from(this.recipes.values()).filter((r) => this.discoveredRecipeIds.has(r.id));
  }

  /**
   * Check if a recipe is discovered.
   */
  isDiscovered(recipeId: string): boolean {
    return this.discoveredRecipeIds.has(recipeId);
  }

  /**
   * Discover a recipe.
   */
  discoverRecipe(recipeId: string): boolean {
    if (!this.recipes.has(recipeId)) {
      return false;
    }
    return this.discoveredRecipeIds.add(recipeId).size > 0;
  }

  /**
   * Sum a recipe's ingredients, treating omitted essence types as 0.
   * `recipe.ingredients` is intentionally `Partial<EssencePool>` (recipes only
   * list the essence types they need), so `resolveTotalEssence` — which expects
   * every key present — can't be called on it directly without producing NaN.
   */
  private getIngredientTotal(ingredients: RecipeDefinition['ingredients']): number {
    return resolveTotalEssence({ ...createEmptyEssencePool(), ...ingredients });
  }

  /**
   * Validate that essences match a recipe exactly.
   */
  validateRecipe(recipe: RecipeDefinition, essences: EssenceAllocation): boolean {
    // Total must match
    const essenceTotal = resolveTotalEssence(essences);
    const recipeTotal = this.getIngredientTotal(recipe.ingredients);
    if (essenceTotal !== recipeTotal) {
      return false;
    }

    // Each required ingredient must match exactly
    return hasMinimumEssence(essences, recipe.ingredients);
  }

  /**
   * Find a recipe by trying to match essences (for discovery/trial and error).
   * Returns a similarity score (0-1) and the matching recipe if found.
   */
  findRecipeMatch(essences: EssenceAllocation): { recipe: RecipeDefinition; similarity: number } | null {
    let bestMatch: { recipe: RecipeDefinition; similarity: number } | null = null;
    const essenceTotal = resolveTotalEssence(essences);

    for (const recipe of this.recipes.values()) {
      const recipeTotal = this.getIngredientTotal(recipe.ingredients);

      // Size must match or be close
      if (essenceTotal !== recipeTotal) {
        continue;
      }

      // Calculate how close this allocation is to the recipe
      let matchCount = 0;
      let totalRequired = 0;

      const recipeIngredients = recipe.ingredients;
      for (const type of ['resonance', 'memory', 'courage', 'sorrow', 'fury', 'truth'] as EssenceType[]) {
        const required = recipeIngredients[type] ?? 0;
        const provided = essences[type] ?? 0;

        if (required > 0) {
          totalRequired++;
          if (provided === required) {
            matchCount++;
          }
        }
      }

      const similarity = totalRequired > 0 ? matchCount / totalRequired : 0;

      if (!bestMatch || similarity > bestMatch.similarity) {
        bestMatch = { recipe, similarity };
      }

      // If we found a perfect match, return immediately
      if (similarity === 1) {
        return bestMatch;
      }
    }

    // Only return a match if it's at least 50% similar
    if (bestMatch && bestMatch.similarity >= 0.5) {
      return bestMatch;
    }

    return null;
  }

  /**
   * Get recipes by size.
   */
  getRecipesBySize(size: 'splinter' | 'fragment' | 'complete'): readonly RecipeDefinition[] {
    return Array.from(this.recipes.values()).filter((r) => r.size === size);
  }

  /**
   * Get recipes by crystal type.
   */
  getRecipesByType(crystalType: string): readonly RecipeDefinition[] {
    return Array.from(this.recipes.values()).filter((r) => r.crystalType === crystalType);
  }
}

/**
 * Create a singleton recipe system.
 */
let recipeSystemInstance: RecipeSystem | null = null;

export function getRecipeSystem(): RecipeSystem {
  if (!recipeSystemInstance) {
    recipeSystemInstance = new RecipeSystem();
  }
  return recipeSystemInstance;
}
